import { EmissionsAbi } from 'abis/Emissions'
import { ePHTRAbi } from 'abis/ePHTR'
import { PHTRAbi } from 'abis/PHTR'
import { NetworkType } from 'abis/set/sets'
import Big from 'big.js'
import { atom } from 'jotai'
import { createAtomsWithQuery } from 'services/query-v2/atomic-factory'
import { DEFAULT_REFETCH_INTERVAL } from 'services/query-v2/refetch-intervals'
import { viemPublicClients } from 'services/wallet/chains'
import { fromBNValue } from 'utils'
import { RPCCallFailedError } from 'utils/errors'
import { Address, zeroAddress } from 'viem'
import { DEFAULT_INDEX_DECIMALS } from '../../../../../app-constants/index'
import { GeneralStakingData } from '../../../../../pages/staking/storage/interface'
import { ethereumCurrencies } from '../../../../../utils/get-acceptable-currencies'
import { QUERY_STAKING_INFO } from '../../../keys'
import { Uint256RPCResponse, Uint8RPCResponse } from '../../../prices/model'
import { rpcResponseErrorBoundary } from '../services'

const parseStakingInformation = (
  decimalsResponse: Uint8RPCResponse,
  distributedPerBlock: Uint256RPCResponse,
  withdrawableResponse: Uint256RPCResponse,
  balanceOfResponse: bigint,
): GeneralStakingData => {
  rpcResponseErrorBoundary([decimalsResponse, distributedPerBlock, withdrawableResponse])

  const distributedPerBlockResult = distributedPerBlock.result
    ? distributedPerBlock.result.toString()
    : '0'
  const withdrawableResult = withdrawableResponse.result
    ? withdrawableResponse.result.toString()
    : '0'
  const balanceOfResult = Big(balanceOfResponse.toString())
  const withdrawable = Big(withdrawableResult)
  const totalPHTRStaked = Big(balanceOfResult).add(withdrawable)
  const distributed = Big(distributedPerBlockResult)
  const blocksPerYear = Big(365).mul(24).mul(60).mul(60).div(15)
  const rewardsPerYear = distributed.mul(blocksPerYear)

  return {
    ytdReturns: totalPHTRStaked.eq(0) ? Big(0) : rewardsPerYear.div(totalPHTRStaked).mul(100),
    totalStaked: fromBNValue(totalPHTRStaked, decimalsResponse.result ?? DEFAULT_INDEX_DECIMALS),
  }
}

const stakingBlockChainQuery = async <T>({
  queryKey: [, address],
}: {
  queryKey: [string, Address]
}) => {
  const ephtrContract = {
    address: address,
    abi: ePHTRAbi,
  }

  const [[decimals, emission]] = await Promise.all([
    viemPublicClients[NetworkType.Mainnet].multicall({
      contracts: [
        {
          ...ephtrContract,
          functionName: 'decimals',
        },
        {
          ...ephtrContract,
          functionName: 'emission',
        },
      ],
    }),
  ])

  if (emission.status === 'failure') throw new RPCCallFailedError(address)

  const emissionsAddress = emission.result
  const emissionsContract = {
    address: emissionsAddress,
    abi: EmissionsAbi,
  }

  const [[distributedPerBlock, withdrawable]] = await Promise.all([
    viemPublicClients[NetworkType.Mainnet].multicall({
      contracts: [
        {
          ...emissionsContract,
          functionName: 'distributedPerBlock',
        },
        {
          ...emissionsContract,
          functionName: 'withdrawable',
        },
      ],
    }),
  ])

  const balanceOf = await viemPublicClients[NetworkType.Mainnet].readContract({
    address: ethereumCurrencies.PHTR.address,
    abi: PHTRAbi,
    functionName: 'balanceOf',
    args: [address],
  })

  return parseStakingInformation(
    decimals,
    distributedPerBlock,
    withdrawable,
    balanceOf as bigint,
  ) as T
}

export const stakingProductRequestFactory = () => {
  const stakingProductAddressAtom = atom<Address>(zeroAddress)

  const mainProperties = createAtomsWithQuery<
    GeneralStakingData,
    Address,
    null,
    null,
    [string, Address]
  >(
    { first: stakingProductAddressAtom },
    QUERY_STAKING_INFO,
    stakingBlockChainQuery<GeneralStakingData>,
    DEFAULT_REFETCH_INTERVAL,
  )

  return { productAddress: stakingProductAddressAtom, ...mainProperties }
}
